import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { Users, Search, Filter } from 'lucide-react';
import {
  ANNUAL_INCOME_OPTIONS,
  BLOOD_TYPES,
  BODY_TYPES,
  DONATION_METHODS,
  EDUCATION_LEVELS,
  IDENTITY_DISCLOSURE_OPTIONS,
  MARITAL_STATUSES,
  PREFECTURES,
  REGION_PREFECTURES,
  TRAVEL_EXPENSE_OPTIONS,
  type RegionName,
  type User,
  type UserStatus,
} from '@kulum/shared';
import { fetchUsers, UserFilters } from '../../lib/userService';
import {
  DataTable,
  Column,
  Badge,
  Button,
  Loading,
  Pagination,
  ResizableDetailLayout,
} from '../../components/ui';
import { UserDetailPanel } from './UserDetailPage';

const PAGE_SIZE = 20;

const inputClassName =
  'w-full rounded-xl border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20';

const numberInputClassName =
  'min-w-0 flex-1 rounded-xl border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20';

const roleOptions = [
  { value: 'donor', label: 'ドナー' },
  { value: 'donee', label: 'ドニー' },
] as const;

const statusOptions: { value: UserStatus; label: string }[] = [
  { value: 'pending', label: '未認証' },
  { value: 'email_verified', label: 'メール認証済み' },
  { value: 'under_review', label: '審査中' },
  { value: 'approved', label: '承認済み' },
  { value: 'rejected', label: '却下' },
  { value: 'suspended', label: '停止中' },
  { value: 'withdrawn', label: '退会済み' },
];

const verificationFilters: { key: keyof UserFilters; label: string }[] = [
  { key: 'identityVerified', label: '本人確認済み' },
  { key: 'educationVerified', label: '学歴証明済み' },
  { key: 'incomeVerified', label: '年収証明済み' },
  { key: 'spermTestVerified', label: '精液検査済み' },
  { key: 'stdTestVerified', label: '性病検査済み' },
  { key: 'hasReviews', label: 'レビューあり' },
];

const activeFilterCount = (filters: UserFilters): number =>
  Object.values(filters).filter((value) => value !== undefined && value !== null && value !== '' && value !== false)
    .length;

interface FilterFieldProps {
  label: string;
  children: ReactNode;
}

function FilterField({ label, children }: FilterFieldProps) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-admin-ink">{label}</span>
      {children}
    </label>
  );
}

interface FilterSectionProps {
  title: string;
  children: ReactNode;
}

function FilterSection({ title, children }: FilterSectionProps) {
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-admin-ink">{title}</h2>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">{children}</div>
    </div>
  );
}

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<UserFilters>({});
  const [draftFilters, setDraftFilters] = useState<UserFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const selectedFilterCount = useMemo(() => activeFilterCount(filters), [filters]);
  const prefectureOptions = useMemo(
    () => (draftFilters.region ? [...REGION_PREFECTURES[draftFilters.region]] : [...PREFECTURES]),
    [draftFilters.region]
  );

  const loadUsers = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      const result = await fetchUsers(
        { ...filters, searchQuery: appliedSearchQuery },
        PAGE_SIZE,
        page
      );

      setUsers(result.users);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, appliedSearchQuery]);

  useEffect(() => {
    void loadUsers(currentPage);
  }, [currentPage, loadUsers]);

  const handleSearch = () => {
    setAppliedSearchQuery(searchQuery);
    setCurrentPage(1);
  };

  const handleFilterChange = (key: keyof UserFilters, value: UserFilters[keyof UserFilters] | undefined) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleRegionChange = (value: string) => {
    const region = value ? (value as RegionName) : undefined;
    setDraftFilters((prev) => ({ ...prev, region, prefecture: undefined }));
  };

  const handleNumberFilterChange = (
    key: 'ageMin' | 'ageMax' | 'heightMin' | 'heightMax' | 'minReviewRating',
    value: string
  ) => {
    const parsed = value === '' ? undefined : Number(value);
    handleFilterChange(key, parsed !== undefined && Number.isFinite(parsed) ? parsed : undefined);
  };

  const handleBooleanFilterChange = (key: keyof UserFilters, checked: boolean) => {
    handleFilterChange(key, checked ? true : undefined);
  };

  const handleApplyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilters({});
    setDraftFilters({});
    setSearchQuery('');
    setAppliedSearchQuery('');
    setCurrentPage(1);
    setShowFilters(false);
  };

  const getStatusBadge = (status: UserStatus) => {
    const variants: Record<UserStatus, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
      pending: 'default',
      email_verified: 'info',
      under_review: 'warning',
      approved: 'success',
      rejected: 'danger',
      suspended: 'danger',
      withdrawn: 'default',
    };

    const labels: Record<UserStatus, string> = {
      pending: '未認証',
      email_verified: 'メール認証済み',
      under_review: '審査中',
      approved: '承認済み',
      rejected: '却下',
      suspended: '停止中',
      withdrawn: '退会済み',
    };

    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  const columns: Column<User>[] = [
    {
      key: 'displayName',
      header: '表示名',
      render: (user) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{user.displayName}</span>
          {user.isDummy === true && (
            <Badge variant="warning" size="sm">
              テスト
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'email',
      header: 'メールアドレス',
    },
    {
      key: 'role',
      header: 'ロール',
      render: (user) => (
        <Badge variant={user.role === 'donor' ? 'info' : 'default'}>
          {user.role === 'donor' ? 'ドナー' : 'ドニー'}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'ステータス',
      render: (user) => getStatusBadge(user.status),
    },
    {
      key: 'createdAt',
      header: '登録日時',
      render: (user) => {
        if (!user.createdAt) return '-';
        const date = user.createdAt.toDate();
        return new Intl.DateTimeFormat('ja-JP', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(date);
      },
    },
  ];

  if (loading && currentPage === 1) {
    return <Loading fullScreen message="ユーザーを読み込み中..." />;
  }

  return (
    <ResizableDetailLayout
      isOpen={Boolean(selectedUserId)}
      detail={
        selectedUserId ? (
          <UserDetailPanel
            userId={selectedUserId}
            embedded
            onClose={() => setSelectedUserId(null)}
            onUpdated={() => void loadUsers(currentPage)}
          />
        ) : null
      }
      list={
        <div className="space-y-6 p-5 md:p-8">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-admin-accent/10 p-3">
            <Users className="h-6 w-6 text-admin-accent" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-admin-ink">ユーザー管理</h1>
            <p className="text-sm text-admin-muted">全ユーザーの管理と検索</p>
          </div>
        </div>
      </div>

      {/* 検索・フィルター */}
      <div className="rounded-2xl border border-admin-border bg-white p-4 shadow-sm">
        <div className="flex gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-admin-muted" />
              <input
                type="text"
                placeholder="メールアドレスまたは表示名で検索"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full rounded-xl border border-admin-border py-2 pl-10 pr-4 text-sm focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20"
              />
            </div>
          </div>
          <Button onClick={handleSearch} leftIcon={<Search className="h-4 w-4" />}>
            検索
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              if (!showFilters) {
                setDraftFilters(filters);
              }
              setShowFilters(!showFilters);
            }}
            leftIcon={<Filter className="h-4 w-4" />}
          >
            フィルター{selectedFilterCount > 0 ? ` (${selectedFilterCount})` : ''}
          </Button>
        </div>

        {/* フィルターパネル */}
        {showFilters && (
          <div className="mt-4 space-y-6 border-t border-admin-border pt-4">
            <FilterSection title="基本条件">
              <FilterField label="ロール">
                <select
                  value={draftFilters.role || ''}
                  onChange={(e) =>
                    handleFilterChange('role', (e.target.value as UserFilters['role']) || undefined)
                  }
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="ステータス">
                <select
                  value={draftFilters.status || ''}
                  onChange={(e) =>
                    handleFilterChange('status', (e.target.value as UserFilters['status']) || undefined)
                  }
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="テストユーザー">
                <select
                  value={draftFilters.isDummy || ''}
                  onChange={(e) =>
                    handleFilterChange('isDummy', (e.target.value as UserFilters['isDummy']) || undefined)
                  }
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  <option value="only">テストのみ</option>
                  <option value="exclude">テストを除外</option>
                </select>
              </FilterField>

              <FilterField label="地域">
                <select
                  value={draftFilters.region || ''}
                  onChange={(e) => handleRegionChange(e.target.value)}
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {Object.keys(REGION_PREFECTURES).map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="都道府県">
                <select
                  value={draftFilters.prefecture || ''}
                  onChange={(e) => handleFilterChange('prefecture', e.target.value || undefined)}
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {prefectureOptions.map((prefecture) => (
                    <option key={prefecture} value={prefecture}>
                      {prefecture}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="年齢">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="18"
                    max="80"
                    value={draftFilters.ageMin ?? ''}
                    onChange={(e) => handleNumberFilterChange('ageMin', e.target.value)}
                    placeholder="下限"
                    className={numberInputClassName}
                  />
                  <span className="text-admin-muted">-</span>
                  <input
                    type="number"
                    min="18"
                    max="80"
                    value={draftFilters.ageMax ?? ''}
                    onChange={(e) => handleNumberFilterChange('ageMax', e.target.value)}
                    placeholder="上限"
                    className={numberInputClassName}
                  />
                </div>
              </FilterField>

              <FilterField label="身長">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="120"
                    max="220"
                    value={draftFilters.heightMin ?? ''}
                    onChange={(e) => handleNumberFilterChange('heightMin', e.target.value)}
                    placeholder="下限"
                    className={numberInputClassName}
                  />
                  <span className="text-admin-muted">-</span>
                  <input
                    type="number"
                    min="120"
                    max="220"
                    value={draftFilters.heightMax ?? ''}
                    onChange={(e) => handleNumberFilterChange('heightMax', e.target.value)}
                    placeholder="上限"
                    className={numberInputClassName}
                  />
                </div>
              </FilterField>
            </FilterSection>

            <FilterSection title="プロフィール">
              <FilterField label="学歴">
                <select
                  value={draftFilters.educationLevel || ''}
                  onChange={(e) => handleFilterChange('educationLevel', e.target.value || undefined)}
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {EDUCATION_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="年収下限">
                <select
                  value={draftFilters.annualIncomeMin || ''}
                  onChange={(e) => handleFilterChange('annualIncomeMin', e.target.value || undefined)}
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {ANNUAL_INCOME_OPTIONS.map((income) => (
                    <option key={income} value={income}>
                      {income}以上
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="血液型">
                <select
                  value={draftFilters.bloodType || ''}
                  onChange={(e) => handleFilterChange('bloodType', e.target.value || undefined)}
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {BLOOD_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}型
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="体型">
                <select
                  value={draftFilters.bodyType || ''}
                  onChange={(e) => handleFilterChange('bodyType', e.target.value || undefined)}
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {BODY_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="婚姻状況">
                <select
                  value={draftFilters.maritalStatus || ''}
                  onChange={(e) => handleFilterChange('maritalStatus', e.target.value || undefined)}
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {MARITAL_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </FilterField>
            </FilterSection>

            <FilterSection title="提供条件">
              <FilterField label="提供方法">
                <select
                  value={draftFilters.donationMethod || ''}
                  onChange={(e) =>
                    handleFilterChange('donationMethod', (e.target.value as UserFilters['donationMethod']) || undefined)
                  }
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {DONATION_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="提供地域">
                <select
                  value={draftFilters.donationArea || ''}
                  onChange={(e) => handleFilterChange('donationArea', e.target.value || undefined)}
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {PREFECTURES.map((prefecture) => (
                    <option key={prefecture} value={prefecture}>
                      {prefecture}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="交通費">
                <select
                  value={draftFilters.travelExpense || ''}
                  onChange={(e) => handleFilterChange('travelExpense', e.target.value || undefined)}
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {TRAVEL_EXPENSE_OPTIONS.filter((option) => option !== '選択してください').map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="身元開示">
                <select
                  value={draftFilters.identityDisclosure || ''}
                  onChange={(e) => handleFilterChange('identityDisclosure', e.target.value || undefined)}
                  className={inputClassName}
                >
                  <option value="">すべて</option>
                  {IDENTITY_DISCLOSURE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FilterField>
            </FilterSection>

            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-admin-ink">認証・レビュー</h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                {verificationFilters.map((filter) => (
                  <label
                    key={filter.key}
                    className="flex min-h-11 items-center gap-2 rounded-xl border border-admin-border bg-admin-bg/50 px-3 py-2 text-sm text-admin-ink"
                  >
                    <input
                      type="checkbox"
                      checked={draftFilters[filter.key] === true}
                      onChange={(e) => handleBooleanFilterChange(filter.key, e.target.checked)}
                      className="h-4 w-4 rounded border-admin-border text-admin-accent focus:ring-admin-accent"
                    />
                    <span>{filter.label}</span>
                  </label>
                ))}
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <FilterField label="最低評価">
                  <select
                    value={draftFilters.minReviewRating ?? ''}
                    onChange={(e) => handleNumberFilterChange('minReviewRating', e.target.value)}
                    className={inputClassName}
                  >
                    <option value="">指定なし</option>
                    <option value="3">3.0以上</option>
                    <option value="4">4.0以上</option>
                    <option value="5">5.0</option>
                  </select>
                </FilterField>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={handleClearFilters} size="sm">
                クリア
              </Button>
              <Button onClick={handleApplyFilters} size="sm">
                適用
              </Button>
            </div>
          </div>
        )}
      </div>

          {/* ユーザー一覧 */}
          {loading ? (
            <Loading message="読み込み中..." />
          ) : (
            <div className="space-y-5">
              <div className="text-xs text-admin-muted">
                表示件数: {users.length} / {totalCount}
              </div>
              <DataTable
                data={users}
                columns={columns}
                onRowClick={(user) => setSelectedUserId(user.id ?? null)}
                emptyMessage="ユーザーが見つかりません"
              />

              {/* ページネーション */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
          )}
        </div>
      }
    />
  );
}
